import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DealerDataService } from '../../services/dealer-data.service';
import { DealerSetup } from '../../models/dealer-setup.model';
import { mapYNFieldsToBooleans } from '../../constants/dealer-setup.constants';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KubeJobsState, FetchCronJobsAction, TriggerCronJobAction } from 'src/app/shared/state/KubeJobsState';
import { CronJobSummaryDTO } from 'src/app/shared/services/models/KubeJobs';
import { KubeJobsService } from 'src/app/shared/services/kubejobs.service';

@Component({
  selector: 'app-point-of-sale',
  templateUrl: './point-of-sale.component.html',
  styleUrl: './point-of-sale.component.scss',
  standalone: false
})
export class POSComponent implements OnInit, OnDestroy {
  posDetailsForm: FormGroup;
  dealerSetupData: DealerSetup;

  public translationService = inject(TranslationService);
  public dealerDataService = inject(DealerDataService);
  public kubeJobsService = inject(KubeJobsService);
  public alertService = inject(AlertService);
  public fb = inject(FormBuilder);
  public store = inject(Store);

  booleanPOSFields = [
    'posIncludeCrnShipping',
    'inventoryResultEnable',
    'posImportFromFile',
    'posImportInvoice',
    'posImportCustomer',
    'posImportInventory',
    'posExportToFile',
    'btdActivated',
    'btdExportToFile'
  ];

  partNumberMappings: { [key: string]: string } = {
    FINISHED_RETREAD_NUM: this.translationService.translate('finishedRetreadNumberText'),
    ZONE_STOCK_NUM: this.translationService.translate('zoneStockNoText'),
    ZONE_CUST_NUM: this.translationService.translate('zoneCustomerNumberText')
  };

  capCasingMappings: { [key: string]: string } = {
    1: this.translationService.translate('onePartText'),
    2: this.translationService.translate('twoPartsText')
  };

  partNumberOptions = [];
  posNameOptions = ['MADDENCO', 'ADAM', 'COSTAR', 'OTHER'];
  capCasingOptions = [];

  // Kube Job names
  readonly IMPORT_JOB = 'pos-master-data-in';
  readonly EXPORT_JOB = 'pos-master-data-out';
  readonly BTD_EXPORT_JOB = 'btd-master-data-out';

  cronJobs$: Observable<CronJobSummaryDTO[]> = inject(Store).select(KubeJobsState.cronJobs);
  kubeJobsLoading$: Observable<boolean> = inject(Store).select(KubeJobsState.loading);

  cronJobs: CronJobSummaryDTO[] = [];
  kubeJobsLoading = false;
  triggeringImport = false;
  triggeringExport = false;
  triggeringBtdExport = false;
  private destroy$ = new Subject<void>();

  get importJobSummary(): CronJobSummaryDTO | undefined {
    return this.cronJobs.find((j) => j.name.endsWith(this.IMPORT_JOB) || j.name === this.IMPORT_JOB);
  }

  get exportJobSummary(): CronJobSummaryDTO | undefined {
    return this.cronJobs.find((j) => j.name.endsWith(this.EXPORT_JOB) || j.name === this.EXPORT_JOB);
  }

  get btdExportJobSummary(): CronJobSummaryDTO | undefined {
    return this.cronJobs.find((j) => j.name.endsWith(this.BTD_EXPORT_JOB) || j.name === this.BTD_EXPORT_JOB);
  }

  constructor() {}

  ngOnInit(): void {
    this.initPOSForm();
    this.loadDealerSetupData();
    this.updateValues();
    this.store.dispatch(new FetchCronJobsAction());
    this.cronJobs$.pipe(takeUntil(this.destroy$)).subscribe((jobs) => (this.cronJobs = jobs));
    this.kubeJobsLoading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => (this.kubeJobsLoading = loading));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  runImport(): void {
    this.triggeringImport = true;
    this.store.dispatch(new TriggerCronJobAction(this.IMPORT_JOB)).subscribe({
      next: () => {
        this.triggeringImport = false;
        this.store.dispatch(new FetchCronJobsAction());
        this.alertService.showAlert({
          type: AlertType.success,
          content: 'Import job triggered successfully.'
        } as Alert);
      },
      error: () => {
        this.triggeringImport = false;
        this.alertService.showAlert({
          type: AlertType.critical,
          content: 'Failed to trigger import job.'
        } as Alert);
      }
    });
  }

  runExport(): void {
    this.triggeringExport = true;
    this.store.dispatch(new TriggerCronJobAction(this.EXPORT_JOB)).subscribe({
      next: () => {
        this.triggeringExport = false;
        this.store.dispatch(new FetchCronJobsAction());
        this.alertService.showAlert({
          type: AlertType.success,
          content: 'Export job triggered successfully.'
        } as Alert);
      },
      error: () => {
        this.triggeringExport = false;
        this.alertService.showAlert({
          type: AlertType.critical,
          content: 'Failed to trigger export job.'
        } as Alert);
      }
    });
  }

  runBtdExport(): void {
    this.triggeringBtdExport = true;
    this.store.dispatch(new TriggerCronJobAction(this.BTD_EXPORT_JOB)).subscribe({
      next: () => {
        this.triggeringBtdExport = false;
        this.store.dispatch(new FetchCronJobsAction());
        this.alertService.showAlert({
          type: AlertType.success,
          content: 'BT Download export job triggered successfully.'
        } as Alert);
      },
      error: () => {
        this.triggeringBtdExport = false;
        this.alertService.showAlert({
          type: AlertType.critical,
          content: 'Failed to trigger BT Download export job.'
        } as Alert);
      }
    });
  }

  loadDealerSetupData(): void {
    this.dealerDataService.getDealerSetup().subscribe({
      next: ({ data }) => {
        if (data) {
          this.dealerSetupData = data;
          this.populateFormWithApiData(this.dealerSetupData);
        }
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.strong,
          content: 'Failed to load dealer setup data. Please try again.'
        } as Alert);
      }
    });
  }

  private initPOSForm(): void {
    this.posDetailsForm = this.fb.group({
      posIncludeCrnShipping: [false],
      inventoryResultEnable: [false],
      posImportInvoice: [false],
      posImportFromFile: [false],
      posExportToFile: [false],
      posImportCustomer: [false],
      posImportInventory: [false],
      btdActivated: [false],
      btdExportToFile: [false],
      posName: ['', [Validators.required]],
      partNumber: ['', [Validators.required]],
      capCasingPartNum: ['', [Validators.required]],
      ioProductNum: ['', [Validators.required]],
      roProductNum: ['', [Validators.required]],
      soProductNum: ['', [Validators.required]],
      adjustmentReturnNum: ['', [Validators.required]],
      adjustmentScrapNum: ['', [Validators.required]],
      npProductNum: ['', [Validators.required]],
      posImportTimeInterval: [''],
      posExportTimeInterval: ['', [Validators.required]],
      posImportStartTime: [''],
      posImportOnce: ['N'],
      btdExportOnce: ['N'],
      btdExportTimeInterval: [''],
      btdExportStartTime: [''],
      btdExportAll: ['']
    });

    // Add dynamic validator for posImportTimeInterval based on posImportOnce
    this.posDetailsForm.get('posImportOnce')?.valueChanges.subscribe((val: string) => {
      const intervalCtrl = this.posDetailsForm.get('posImportTimeInterval');
      const startTimeCtrl = this.posDetailsForm.get('posImportStartTime');
      if (val === 'Y') {
        startTimeCtrl?.setValidators([Validators.required]);
        intervalCtrl?.clearValidators();
      } else {
        intervalCtrl?.setValidators([Validators.required]);
        startTimeCtrl?.clearValidators();
      }
      intervalCtrl?.updateValueAndValidity();
      startTimeCtrl?.updateValueAndValidity();
    });

    // Add dynamic validator for btdExportStartTime based on btdExportOnce
    this.posDetailsForm.get('btdExportOnce')?.valueChanges.subscribe((val: string) => {
      const intervalCtrlBtd = this.posDetailsForm.get('btdExportTimeInterval');
      const startTimeCtrlBtd = this.posDetailsForm.get('btdExportStartTime');
      if (val === 'Y') {
        startTimeCtrlBtd?.setValidators([Validators.required]);
        intervalCtrlBtd?.clearValidators();
      } else {
        intervalCtrlBtd?.setValidators([Validators.required]);
        startTimeCtrlBtd?.clearValidators();
      }
      intervalCtrlBtd?.updateValueAndValidity();
      startTimeCtrlBtd?.updateValueAndValidity();
    });
  }

  private convertUTCToLocal(dateTimeStr: string): string {
    if (!dateTimeStr) return '';
    const utcDate = new Date(dateTimeStr + 'Z'); // Ensure it's parsed as UTC
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    const hours = String(utcDate.getHours()).padStart(2, '0');
    const minutes = String(utcDate.getMinutes()).padStart(2, '0');
    const seconds = String(utcDate.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  private convertLocalToUTC(dateTimeStr: string): string {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toISOString();
  }

  formatDateWithTimezone(utcDateString: string): string {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);

    // Format the date and time in local timezone
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });

    return dateFormatter.format(date);
  }

  populateFormWithApiData(data: DealerSetup): void {
    this.posDetailsForm.patchValue({
      ...mapYNFieldsToBooleans(data, this.booleanPOSFields),
      posName: data.posName,
      partNumber: data.partNumber,
      capCasingPartNum: data.capCasingPartNum,
      ioProductNum: data.ioProductNum,
      roProductNum: data.roProductNum,
      soProductNum: data.soProductNum,
      adjustmentReturnNum: data.adjustmentReturnNum,
      adjustmentScrapNum: data.adjustmentScrapNum,
      npProductNum: data.npProductNum,
      posImportTimeInterval: data.posImportTimeInterval,
      posExportTimeInterval: data.posExportTimeInterval,
      posImportStartTime: this.convertUTCToLocal(data.posImportStartTime),
      posImportOnce: data.posImportOnce,
      btdExportOnce: data.btdExportOnce,
      btdExportTimeInterval: data.btdExportTimeInterval,
      btdExportStartTime: this.convertUTCToLocal(data.btdExportStartTime),
      btdExportAll: data.btdExportAll
    });
  }

  private updateValues(): void {
    this.partNumberOptions = [
      this.translationService.translate('finishedRetreadNumberText'),
      this.translationService.translate('zoneStockNoText'),
      this.translationService.translate('zoneCustomerNumberText')
    ];
    this.capCasingOptions = [this.translationService.translate('onePartText'), this.translationService.translate('twoPartsText')];
    this.posDetailsForm.get('partNumber')?.valueChanges.subscribe((val: string) => {
      if (val && this.partNumberMappings[val]) {
        this.posDetailsForm.get('partNumber')?.patchValue(this.partNumberMappings[val], { emitEvent: false });
      }
    });
    this.posDetailsForm.get('capCasingPartNum')?.valueChanges.subscribe((val: string) => {
      if (val && this.capCasingMappings[val]) {
        this.posDetailsForm.get('capCasingPartNum')?.patchValue(this.capCasingMappings[val], { emitEvent: false });
      }
    });
  }

  save() {
    if (!this.posDetailsForm.valid) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: 'Please fill all required fields.'
      } as Alert);
      return;
    }

    // Get raw form values
    const formValues = this.posDetailsForm.getRawValue();

    // Convert boolean fields to 'Y' or 'N'
    const payload: DealerSetup = { ...formValues };
    this.booleanPOSFields.forEach((field) => {
      if (field in payload) {
        payload[field] = payload[field] ? 'Y' : 'N';
      }
    });

    if (payload.posImportStartTime) {
      payload.posImportStartTime = this.convertLocalToUTC(payload.posImportStartTime);
    }

    if (payload.btdExportStartTime) {
      payload.btdExportStartTime = this.convertLocalToUTC(payload.btdExportStartTime);
    }

    const partNumberKey = Object.keys(this.partNumberMappings).find((key) => this.partNumberMappings[key] === payload.partNumber);
    if (partNumberKey) {
      payload.partNumber = partNumberKey;
    }

    // For capCasingPartNum
    const capCasingKey = Object.keys(this.capCasingMappings).find((key) => this.capCasingMappings[key] === payload.capCasingPartNum);
    if (capCasingKey) {
      payload.capCasingPartNum = capCasingKey;
    }

    this.dealerDataService.updatePosSetup(payload).subscribe({
      next: ({ data }) => {
        this.dealerSetupData = data;
        this.kubeJobsService.updateCronSchedules(data);
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
      },
      error: () => {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }
}
